/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ------------------------------------------------------------------------------------------------------------
 * RING SIZE CHANGE BLOCK
 *
 * Renders a checkbox + a size selector on ring product pages. When the shopper opts in:
 *   - the chosen size note is sent as a line item property on the main product
 *   - the matching variant of a separate "size change" product (configured in the block) is added to the
 *     cart at the same time
 *   - no selection triggers an inline warning instead of submitting
 * The selector is built from the size-change product's variations: the ring's custom.material metafield is
 * matched to the 素材 (material) option server-side, so the shopper only chooses the 号数 (size) variation.
 * When the size-change product has no variations, the block falls back to a free-text size field.
 * The block only renders when the product carries the merchant-configured tag (see block schema).
 * ------------------------------------------------------------------------------------------------------------ */
class RingSizeChange extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-ring-size-change-toggle]');
    this.select = this.querySelector('[data-ring-size-change-select]');
    this.input = this.querySelector('[data-ring-size-change-input]');
    this.fields = this.querySelector('[data-ring-size-change-fields]');
    this.warning = this.querySelector('[data-ring-size-change-warning]');
    this.notice = this.querySelector('[data-ring-size-change-notice]');
    this.priceEl = this.querySelector('[data-ring-size-change-price]');

    this.form = document.getElementById(this.getAttribute('form-id'));
    this.propertyName = this.getAttribute('property-name') || 'サイズ変更';
    this.priceLabel = this.getAttribute('price-label') || '追加料金';
    // Only used by the free-text fallback (size-change product without variations).
    this.fallbackVariantId = parseInt(this.getAttribute('size-change-variant-id'), 10) || null;
    this.fallbackPriceMoney = this.getAttribute('size-change-price') || '';

    // The shopper picks a variant from the <select>; the text input is the legacy fallback.
    this.control = this.select || this.input;

    if (!this.toggle || !this.control || !this.form) return;

    this.toggle.addEventListener('change', this._onToggle.bind(this));
    this.control.addEventListener(this.select ? 'change' : 'input', this._onControlChange.bind(this));
    // Capture-phase so we run before the theme's <product-form> submit handler.
    this.form.addEventListener('submit', this._onSubmit.bind(this), true);
  }

  _onToggle() {
    const checked = this.toggle.checked;
    this.fields.hidden = !checked;
    this.control.disabled = !checked;
    if (this.notice) {
      this.notice.hidden = !checked;
    }
    if (!checked) {
      this.warning.hidden = true;
      if (this.select) this.select.selectedIndex = 0;
      if (this.input) this.input.value = '';
    } else {
      this.control.focus();
    }
    this._updatePrice();
  }

  _onControlChange() {
    if (this._selectedValue() !== '') {
      this.warning.hidden = true;
    }
    this._updatePrice();
  }

  // Show the additional charge for the chosen size-change variant.
  _updatePrice() {
    if (!this.priceEl) return;

    let money = '';
    if (this.toggle.checked) {
      if (this.select) {
        const option = this.select.options[this.select.selectedIndex];
        money = option && option.value ? option.getAttribute('data-price-money') || '' : '';
      } else {
        money = this.fallbackPriceMoney;
      }
    }

    if (money) {
      this.priceEl.textContent = `${this.priceLabel}：${money}`;
      this.priceEl.hidden = false;
    } else {
      this.priceEl.hidden = true;
    }
  }

  // Raw value used to decide whether the shopper has made a choice.
  _selectedValue() {
    if (this.select) return this.select.value || '';
    return (this.input.value || '').trim();
  }

  // Variant of the size-change product to add to the cart.
  _selectedVariantId() {
    if (this.select) return parseInt(this.select.value, 10) || null;
    return this.fallbackVariantId;
  }

  // Human-readable size note recorded as a line item property.
  _selectedLabel() {
    if (this.select) {
      const option = this.select.options[this.select.selectedIndex];
      return option ? option.textContent.trim() : '';
    }
    return (this.input.value || '').trim();
  }

  async _onSubmit(event) {
    if (!this.toggle.checked) return;

    const variantId = this._selectedVariantId();
    const label = this._selectedLabel();
    const isValid = this.select ? Boolean(variantId) : label !== '';

    if (!isValid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.warning.hidden = false;
      this.control.focus();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitButtons = Array.from(this.form.elements).filter((el) => el.type === 'submit');
    submitButtons.forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-busy', 'true');
    });

    const formData = new FormData(this.form);
    const mainProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) mainProperties[match[1]] = value;
    }
    // Record the requested size on the ring line as well.
    if (label) mainProperties[this.propertyName] = label;

    const mainVariantId = parseInt(formData.get('id'), 10);
    const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;

    const items = [
      {
        id: mainVariantId,
        quantity: quantity,
        properties: mainProperties
      }
    ];
    // Add the matching size-change variant alongside the ring.
    if (variantId) {
      items.push({
        id: variantId,
        quantity: quantity,
        properties: { [this.propertyName]: label }
      });
    }

    let response;
    let responseJson;
    try {
      response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items, sections: ['mini-cart'] })
      });
      responseJson = await response.json();
    } finally {
      submitButtons.forEach((btn) => {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
      });
    }

    if (response.ok) {
      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      this.form.dispatchEvent(new CustomEvent('variant:added', {
        bubbles: true,
        detail: {
          variant: responseJson.items ? responseJson.items[0] : responseJson
        }
      }));

      const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
      const cartContent = await cartResponse.json();

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent }
      }));

      cartContent.sections = responseJson.sections;
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          openMiniCart: window.themeVariables.settings.cartType === 'drawer' && this.form.closest('.drawer') === null
        }
      }));
    } else {
      this.form.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    this.form.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: {
        status: response.ok ? 'success' : 'error',
        error: (responseJson && responseJson.description) || ''
      }
    }));
  }
}

if (!window.customElements.get('ring-size-change')) {
  window.customElements.define('ring-size-change', RingSizeChange);
}

/* ------------------------------------------------------------------------------------------------------------
 * RESIZE VARIANT OPTION BLOCK
 *
 * Renders a checkbox + a size selector on ring product pages, same shape as <ring-size-change> above, but
 * the selectable sizes come from the *currently selected base variant's own* bellis.resize_options metafield
 * (a list.variant_reference to "resize fee" product variants) instead of one product-wide option set. This
 * is what lets two variants of the same product (e.g. size 6 and size 8) expose different resize choices.
 *
 * The base variant -> options list mapping is embedded once as JSON (data-resize-options-map) so no extra
 * network request is needed when the shopper switches sizes.
 *
 * The theme's variant picker updates the URL (history.replaceState with ?variant=<id>) and then fires
 * "variant:changed" on the product form; this element listens for that to rebuild the dropdown for the size
 * actually selected, and resets the toggle so a resize choice made for a previous size is never carried
 * forward. At submit time it re-derives the base variant id from the form itself (not from the last
 * "variant:changed" event) and re-validates that the chosen resize variant is still listed for that variant,
 * so the sub-product added to the cart always matches the size actually being purchased.
 * ------------------------------------------------------------------------------------------------------------ */
class ResizeVariantOption extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-resize-toggle]');
    this.select = this.querySelector('[data-resize-select]');
    this.fields = this.querySelector('[data-resize-fields]');
    this.warning = this.querySelector('[data-resize-warning]');
    this.notice = this.querySelector('[data-resize-notice]');
    this.priceEl = this.querySelector('[data-resize-price]');
    const mapScript = this.querySelector('[data-resize-options-map]');
    const baseSizesScript = this.querySelector('[data-resize-base-sizes]');

    this.form = document.getElementById(this.getAttribute('form-id'));
    this.propertyName = this.getAttribute('property-name') || 'サイズお直し';
    // price-label / placeholder-text は空欄（管理画面でラベルを消したい場合）を尊重するため
    // 属性が未設定（null）の時だけデフォルトを補う。空文字列はそのまま使う。
    this.priceLabel = this.getAttribute('price-label') ?? '追加料金';
    this.placeholderText = this.getAttribute('placeholder-text') ?? '選択してください';

    if (!this.toggle || !this.select || !this.form || !mapScript) return;

    try {
      this.optionsMap = JSON.parse(mapScript.textContent);
    } catch (e) {
      this.optionsMap = {};
    }

    // ベース号数（product.selected_or_first_available_variant の号数オプション値、例:「8号」）を
    // variantId ごとに持つマップ。サイズオプションが特定できなかった商品では全キーが null になり、
    // 最終号数表示は自動的に出さない（例外を投げず、既存の差分表記のみに静かにフォールバックする）。
    try {
      this.baseSizes = baseSizesScript ? JSON.parse(baseSizesScript.textContent) : {};
    } catch (e) {
      this.baseSizes = {};
    }

    this.currentVariantId = String(this.getAttribute('initial-variant-id') || '');
    this._renderOptionsFor(this.currentVariantId);

    this.toggle.addEventListener('change', this._onToggle.bind(this));
    this.select.addEventListener('change', this._onControlChange.bind(this));
    this.form.addEventListener('submit', this._onSubmit.bind(this), true);
    document.addEventListener('variant:changed', this._onVariantChanged.bind(this));
  }

  _onVariantChanged(event) {
    if (event.target !== this.form) return;
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    const newVariantId = String(variant.id);
    if (newVariantId === this.currentVariantId) return;

    this.currentVariantId = newVariantId;
    // The previous size's resize choice (a specific fee variant) does not necessarily exist in the new
    // size's option list, so drop the selection instead of silently carrying a stale one forward.
    this.toggle.checked = false;
    this._renderOptionsFor(this.currentVariantId);
    this._onToggle();
  }

  _renderOptionsFor(variantId) {
    const options = (this.optionsMap && this.optionsMap[String(variantId)]) || [];

    this.select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.placeholderText;
    this.select.appendChild(placeholder);

    options.forEach((opt) => {
      const el = document.createElement('option');
      el.value = opt.id;
      el.dataset.price = opt.price;
      el.dataset.priceMoney = opt.priceMoney;
      if (opt.available === false) el.disabled = true;
      let label = opt.label;
      const finalSize = this._finalSizeLabel(variantId, opt.label);
      if (finalSize) label = `（${opt.label}）${finalSize}`;
      if (opt.price > 0) label += `（＋${opt.priceMoney}）`;
      if (opt.available === false) label += '（在庫切れ）';
      el.textContent = label;
      this.select.appendChild(el);
    });

    const hasSelectableOptions = options.some((opt) => opt.available !== false);
    this.toggle.disabled = !hasSelectableOptions;
    this.classList.toggle('is-unavailable', !hasSelectableOptions);

    this._updatePrice();
  }

  _onToggle() {
    const checked = this.toggle.checked;
    this.fields.hidden = !checked;
    this.select.disabled = !checked;
    if (this.notice) this.notice.hidden = !checked;
    if (!checked) {
      this.warning.hidden = true;
      this.select.selectedIndex = 0;
    } else {
      this.select.focus();
    }
    this._updatePrice();
  }

  _onControlChange() {
    if (this.select.value !== '') this.warning.hidden = true;
    this._updatePrice();
  }

  _updatePrice() {
    if (!this.priceEl) return;
    let money = '';
    if (this.toggle.checked) {
      const option = this.select.options[this.select.selectedIndex];
      money = option && option.value ? option.dataset.priceMoney || '' : '';
    }
    if (money) {
      this.priceEl.textContent = this.priceLabel ? `${this.priceLabel}：${money}` : money;
      this.priceEl.hidden = false;
    } else {
      this.priceEl.hidden = true;
    }
  }

  // ラベル文字列（例:「8号」「+1号」「－2号」）から号数を数値として取り出す。全角数字・全角＋－にも
  // 対応するが、パースできない表記（例:「フリーサイズ」）は例外を投げず null を返すだけにする。
  // 呼び出し側はこの null を「最終号数は計算しない（表示しない）」の合図として扱う。
  _parseSizeNumber(text) {
    if (typeof text !== 'string' || text === '') return null;
    const normalized = text
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/[＋]/g, '+')
      .replace(/[－―ー]/g, '-');
    const match = normalized.match(/[+-]?\d+(?:\.\d+)?/);
    if (!match) return null;
    const num = parseFloat(match[0]);
    return Number.isFinite(num) ? num : null;
  }

  // ベース号数（バリアントの号数オプション値）＋選択した差分（お直し料金バリアントのtitle）から
  // 最終号数を計算する。どちらかがパースできない場合は null を返し、呼び出し側は差分のみの
  // 従来表示にそのままフォールバックする（表示しないだけで、機能が壊れることはない）。
  _finalSizeLabel(variantId, diffLabelText) {
    const baseSizeText = this.baseSizes && this.baseSizes[String(variantId)];
    const baseNum = this._parseSizeNumber(baseSizeText);
    const diffNum = this._parseSizeNumber(diffLabelText);
    if (baseNum === null || diffNum === null) return null;
    const finalNum = baseNum + diffNum;
    if (!Number.isFinite(finalNum)) return null;
    return `${finalNum}号`;
  }

  _selectedOptionVariantId() {
    return parseInt(this.select.value, 10) || null;
  }

  _selectedLabel() {
    const option = this.select.options[this.select.selectedIndex];
    return option ? option.textContent.trim() : '';
  }

  async _onSubmit(event) {
    if (!this.toggle.checked) return;

    // Re-read the base variant id from the form itself at submit time (rather than trusting
    // this.currentVariantId alone), so a resize choice can never be sent for a size other than the
    // one about to actually be purchased, even if variant:changed fired out of order.
    const formVariantId = String(new FormData(this.form).get('id'));
    if (formVariantId !== this.currentVariantId) {
      this.currentVariantId = formVariantId;
      this._renderOptionsFor(formVariantId);
    }

    const optionVariantId = this._selectedOptionVariantId();
    const label = this._selectedLabel();

    if (!optionVariantId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.warning.hidden = false;
      this.select.focus();
      return;
    }

    // The chosen sub-product must still be listed for the base variant actually being purchased.
    const validOptions = (this.optionsMap && this.optionsMap[this.currentVariantId]) || [];
    const isStillValid = validOptions.some((opt) => String(opt.id) === String(optionVariantId));
    if (!isStillValid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.warning.hidden = false;
      this.select.focus();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitButtons = Array.from(this.form.elements).filter((el) => el.type === 'submit');
    submitButtons.forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-busy', 'true');
    });

    const formData = new FormData(this.form);
    const mainProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) mainProperties[match[1]] = value;
    }

    if (label) mainProperties[this.propertyName] = label;

    const mainVariantId = parseInt(formData.get('id'), 10);
    const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;

    // Attach the resize-fee line as a native nested cart line (Cart AJAX API `parent_id`, API
    // version 2025-10+) instead of a same-level second line tied together only by a matching
    // property. Nested lines are guaranteed by the platform to be removed automatically when their
    // parent line is removed, so a resize-fee-only line can never survive on its own in the cart.
    const items = [
      {
        id: mainVariantId,
        quantity: quantity,
        properties: mainProperties
      },
      {
        id: optionVariantId,
        quantity: quantity,
        parent_id: mainVariantId,
        properties: {
          [this.propertyName]: label
        }
      }
    ];

    let response;
    let responseJson;
    try {
      response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items, sections: ['mini-cart'] })
      });
      responseJson = await response.json();
    } finally {
      submitButtons.forEach((btn) => {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
      });
    }

    if (response.ok) {
      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      this.form.dispatchEvent(new CustomEvent('variant:added', {
        bubbles: true,
        detail: {
          variant: responseJson.items ? responseJson.items[0] : responseJson
        }
      }));

      const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
      const cartContent = await cartResponse.json();

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent }
      }));

      cartContent.sections = responseJson.sections;
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          openMiniCart: window.themeVariables.settings.cartType === 'drawer' && this.form.closest('.drawer') === null
        }
      }));
    } else {
      this.form.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    this.form.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: {
        status: response.ok ? 'success' : 'error',
        error: (responseJson && responseJson.description) || ''
      }
    }));
  }
}

if (!window.customElements.get('resize-variant-option')) {
  window.customElements.define('resize-variant-option', ResizeVariantOption);
}